import React, { useState, useEffect } from 'react';
import { Leaf, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

function KolikoDanaDoJeseni() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [autumnDate, setAutumnDate] = useState(new Date());
  
  // Calculate autumn start date (September 23rd of current year)
  useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();
    const autumnStart = new Date(year, 8, 23); // September 23rd (month is 0-indexed)
    
    // If autumn has already started this year, set to next year
    if (now > autumnStart) {
      autumnStart.setFullYear(year + 1);
    }
    
    setAutumnDate(autumnStart);
    setCurrentYear(now.getFullYear());
  }, []);
  
  // Update countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const difference = autumnDate.getTime() - now.getTime();
      
      if (difference <= 0) {
        clearInterval(timer);
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        });
        return;
      }
      
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      
      setTimeLeft({ days, hours, minutes, seconds });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [autumnDate]);
  
  // Format number with leading zero
  const formatNumber = (num: number): string => {
    return num.toString().padStart(2, '0');
  };
  
  // Calculate percentage of time passed until autumn
  const calculateProgress = (): number => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    // Start from either last autumn or beginning of year
    const startDate = new Date(currentYear, 0, 1); // January 1st
    const lastAutumn = new Date(currentYear, 8, 23);
    
    if (now > lastAutumn) {
      startDate.setTime(lastAutumn.getTime());
    }
    
    const totalTime = autumnDate.getTime() - startDate.getTime();
    const elapsedTime = now.getTime() - startDate.getTime();
    
    return Math.min(100, Math.max(0, (elapsedTime / totalTime) * 100));
  };
  
  // Get season information
  const getCurrentSeason = (): string => {
    const now = new Date();
    const month = now.getMonth();
    
    if (month >= 2 && month < 5) return 'proljeće';
    if (month >= 5 && month < 8) return 'ljeto';
    if (month >= 8 && month < 11) return 'jesen';
    return 'zima';
  };
  
  const getSeasonEmoji = (season: string): string => {
    switch (season) {
      case 'proljeće': return '🌱';
      case 'ljeto': return '☀️';
      case 'jesen': return '🍂';
      case 'zima': return '❄️';
      default: return '';
    }
  };
  
  const season = getCurrentSeason();
  const progress = calculateProgress();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Koliko Dana Do Jeseni",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Precizno odbrojavanje do početka jeseni. Saznajte točno koliko dana, sati, minuta i sekundi je ostalo do jeseni.",
    "url": "https://online-kalkulator.com/online-stoperica/koliko-dana-do-jeseni",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Precizno odbrojavanje do jeseni",
      "Prikaz dana, sati, minuta i sekundi",
      "Vizualni prikaz napretka",
      "Informacije o godišnjim dobima",
      "Automatsko ažuriranje"
    ]
  };

  return (
    <>
      <SEO 
        title="Koliko Dana Do Jeseni | Odbrojavanje Do Jeseni"
        description="Precizno odbrojavanje do početka jeseni. Saznajte točno koliko dana, sati, minuta i sekundi je ostalo do jeseni."
        canonicalUrl="/online-stoperica/koliko-dana-do-jeseni"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Leaf className="w-8 h-8 text-orange-500" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Koliko Dana Do Jeseni</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Countdown Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Season Info */}
              <div className="text-center mb-4">
                <div className="inline-block px-4 py-2 bg-blue-50 rounded-full">
                  <span className="text-lg font-medium text-blue-700">
                    Trenutno godišnje doba: <span className="font-bold">{season} {getSeasonEmoji(season)}</span>
                  </span>
                </div>
              </div>
              
              {/* Target Date */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-gray-600 mb-2">
                  <Calendar className="w-5 h-5" />
                  <span>Jesen počinje: {autumnDate.toLocaleDateString('hr-HR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-4 mb-6">
                <div 
                  className="h-full bg-gradient-to-r from-green-500 via-yellow-500 to-orange-500 rounded-full transition-all duration-1000"
                  style={{ width: `${progress}%` }}
                />
              </div>

              {/* Countdown Display */}
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="text-4xl font-bold text-orange-600 font-mono">
                    {timeLeft.days}
                  </div>
                  <div className="text-gray-600 text-sm mt-1">Dana</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="text-4xl font-bold text-orange-600 font-mono">
                    {formatNumber(timeLeft.hours)}
                  </div>
                  <div className="text-gray-600 text-sm mt-1">Sati</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="text-4xl font-bold text-orange-600 font-mono">
                    {formatNumber(timeLeft.minutes)}
                  </div>
                  <div className="text-gray-600 text-sm mt-1">Minuta</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="text-4xl font-bold text-orange-600 font-mono">
                    {formatNumber(timeLeft.seconds)}
                  </div>
                  <div className="text-gray-600 text-sm mt-1">Sekundi</div>
                </div>
              </div>
              
              {/* Autumn Message */}
              {season === 'jesen' && (
                <div className="text-center py-3 px-4 bg-orange-50 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <Leaf className="w-5 h-5 text-orange-500" />
                    <p className="text-lg font-medium text-orange-700">Jesen je već počela! Uživajte u šarenim bojama i svježem zraku!</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Odbrojavanju Do Jeseni</h2>
                <p className="text-gray-600 leading-relaxed">
                  Odbrojavanje do jeseni je zabavan način da pratite koliko je vremena preostalo do
                  službenog početka jeseni. Jesen astronomski počinje na jesenski ekvinocij, koji pada
                  oko 23. rujna na sjevernoj hemisferi. Ovaj datum označava trenutak kada dan i noć
                  traju približno jednako dugo, a nakon toga dani postaju kraći od noći.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Godišnja Doba</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🌱</span>
                      <h3 className="text-lg font-medium text-green-700">Proljeće</h3>
                    </div>
                    <p className="text-gray-600">
                      Počinje oko 20. ožujka i traje do 21. lipnja. Karakteriziraju ga umjerene temperature
                      i buđenje prirode nakon zime.
                    </p>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">☀️</span>
                      <h3 className="text-lg font-medium text-yellow-700">Ljeto</h3>
                    </div>
                    <p className="text-gray-600">
                      Počinje oko 21. lipnja i traje do 23. rujna. Najduži dani u godini, visoke
                      temperature i vrijeme za odmor i uživanje na otvorenom.
                    </p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🍂</span>
                      <h3 className="text-lg font-medium text-orange-700">Jesen</h3>
                    </div>
                    <p className="text-gray-600">
                      Počinje oko 23. rujna i traje do 21. prosinca. Karakteriziraju je promjene boja
                      lišća, umjerene temperature i berba plodova.
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">❄️</span>
                      <h3 className="text-lg font-medium text-blue-700">Zima</h3>
                    </div>
                    <p className="text-gray-600">
                      Počinje oko 21. prosinca i traje do 20. ožujka. Najkraći dani u godini, niske
                      temperature i često snijeg u kontinentalnim područjima.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Zanimljivosti o Jeseni</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Jesenski ekvinocij označava trenutak kada Sunce prelazi preko nebeskog ekvatora</li>
                  <li>Lišće mijenja boje zbog smanjenja klorofila i otkrivanja drugih pigmenata</li>
                  <li>Jesen je tradicionalno vrijeme žetve i berbe plodova</li>
                  <li>Mnoge životinje počinju pripreme za zimski san ili migraciju</li>
                  <li>U južnoj hemisferi, jesen počinje oko 20. ožujka</li>
                  <li>Dani postaju kraći, a noći duže tijekom jeseni</li>
                  <li>Jesen je inspirirala mnoge umjetnike, pjesnike i skladatelje</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti Odbrojavanje</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Odbrojavanje se automatski ažurira svake sekunde</li>
                  <li>Prikazuje točan broj dana, sati, minuta i sekundi do početka jeseni</li>
                  <li>Traka napretka vizualno prikazuje koliko je vremena prošlo od zadnje jeseni ili početka godine</li>
                  <li>Ako je trenutno jesen, odbrojavanje će pokazivati vrijeme do sljedeće jeseni</li>
                  <li>Stranica se automatski ažurira svake godine za novo odbrojavanje</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Planiranje Jesenskih Aktivnosti</h2>
                <div className="space-y-3">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-1">Priroda i vanjski prostor</h3>
                    <p className="text-gray-600 text-sm">
                      Iskoristite odbrojavanje za planiranje izleta u prirodu kako biste uživali u jesenskim bojama, 
                      branju jabuka, posjetu vinogradima ili šetnjama kroz šumu.
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-1">Uređenje doma</h3>
                    <p className="text-gray-600 text-sm">
                      Planirajte jesensko uređenje doma, pripremu vrta za zimu ili sadnju biljaka koje cvjetaju u proljeće.
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-1">Kulinarske aktivnosti</h3>
                    <p className="text-gray-600 text-sm">
                      Pripremite se za sezonu bundeva, jabuka i toplih napitaka. Planirajte pripremu zimnice i jesenskih recepata.
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-1">Festivali i događanja</h3>
                    <p className="text-gray-600 text-sm">
                      Istražite i planirajte posjete jesenskim festivalima, berbi grožđa i drugim sezonskim događanjima.
                    </p>
                  </div>
                </div>
              </section>

              {/* Interlinking Section */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Ostala Odbrojavanja</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Link to="/online-stoperica/koliko-dana-do-zime" className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">❄️</span>
                      <h3 className="text-lg font-medium text-blue-700">Koliko Dana Do Zime</h3>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Saznajte koliko je dana ostalo do početka zime.
                    </p>
                  </Link>
                  <Link to="/online-stoperica/koliko-dana-do-proljeca" className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🌱</span>
                      <h3 className="text-lg font-medium text-green-700">Koliko Dana Do Proljeća</h3>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Saznajte koliko je dana ostalo do početka proljeća.
                    </p>
                  </Link>
                  <Link to="/online-stoperica/koliko-dana-do-ljeta" className="p-4 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">☀️</span>
                      <h3 className="text-lg font-medium text-yellow-700">Koliko Dana Do Ljeta</h3>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Saznajte koliko je dana ostalo do početka ljeta.
                    </p>
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KolikoDanaDoJeseni;